import React from 'react';
import styles from './JobDetails.module.css';
import * as utils from '../../../utils/utils';
import Sidebar from '../../Sidebar/Sidebar';

const SELECTED_JOB_STORAGE_KEY = 'linkedupSelectedJob';

export default class JobDetails extends React.Component {

	constructor(props) {
		super(props);

		this.state = {
			job: null,
			gapData: null,
			isLoadingGaps: false,
			errorText: '',
		};
	}

	componentDidMount() {
		if (!utils.isLoggedIn()) {
			window.location.href = '/signin';
			return;
		}

		const params = new URLSearchParams(window.location.search);
		const queryJobID = Number(params.get('jobId'));

		const rawValue = window.localStorage.getItem(SELECTED_JOB_STORAGE_KEY);
		let selectedJob = null;

		if (rawValue) {
			try {
				selectedJob = JSON.parse(rawValue);
			}
			catch (error) {
				selectedJob = null;
			}
		}

		if (!selectedJob || !Number.isInteger(Number(selectedJob.jobId))) {
			window.location.href = '/job-list';
			return;
		}

		if (Number.isInteger(queryJobID) && queryJobID > 0 && Number(selectedJob.jobId) !== queryJobID) {
			window.location.href = '/job-list';
			return;
		}

		this.setState({
			job: selectedJob,
		}, () => {
			this.loadJobGaps(Number(selectedJob.jobId));
		});
	}

	loadJobGaps = (jobID) => {
		if (!Number.isInteger(jobID) || jobID <= 0) {
			return;
		}

		this.setState({
			isLoadingGaps: true,
			errorText: '',
		});

		utils.sendRequest('/job/getRecommendedJobGaps', {
			jobID,
		})
			.then((responseData) => {
				this.setState({
					gapData: responseData,
					isLoadingGaps: false,
				});
			})
			.catch(() => {
				this.setState({
					gapData: null,
					isLoadingGaps: false,
				});
			});
	};

	goBack = () => {
		window.location.href = '/job-list';
	};

	handleGenerateCourseForGap = (gapType, gapName) => {
		const safeType = (gapType || '').toString().trim();
		const safeName = (gapName || '').toString().trim();

		if (safeName === '') {
			return;
		}

		const topic = safeType === '' ? safeName : `${safeType}: ${safeName}`;
		window.location.href = `/my-courses?topic=${encodeURIComponent(topic)}`;
	};

	formatCompensation = (job) => {
		const toNumber = (value) => {
			if (value === null || value === undefined || value === '') {
				return null;
			}

			const parsed = Number(value);
			return Number.isFinite(parsed) ? parsed : null;
		};

		const min = toNumber(job.salaryMin);
		const max = toNumber(job.salaryMax);

		if (min === null && max === null) {
			return 'Compensation not listed';
		}

		const formatter = new Intl.NumberFormat(undefined, {
			style: 'currency',
			currency: job.currency || 'USD',
			maximumFractionDigits: 0,
		});

		if (min !== null && max !== null) {
			return `${formatter.format(min)} - ${formatter.format(max)}`;
		}

		if (min !== null) {
			return `From ${formatter.format(min)}`;
		}

		return `Up to ${formatter.format(max)}`;
	};

	formatLocation = (job) => {
		const values = [job.city, job.state, job.country].filter((value) => value && value.trim() !== '');
		if (values.length === 0) {
			return 'Location not listed';
		}

		return values.join(', ');
	};

	renderGapList = (title, items) => {
		if (!Array.isArray(items) || items.length === 0) {
			return null;
		}

		return (
			<div className={styles.gapSection}>
				<h4 className={styles.gapTitle}>{title}</h4>
				<div className={styles.gapList}>
					{items.slice(0, 6).map((item, index) => (
						<div key={`${title}-${item.name}-${index}`} className={styles.gapItem}>
							<div className={styles.gapInfo}>
								<span>{item.name}</span>
								<span className={styles.gapMetric}>{item.numGapLeft} gap</span>
							</div>

							<button
								type='button'
								className={styles.generateCourseButton}
								onClick={() => this.handleGenerateCourseForGap(title, item.name)}
							>
								Generate a Course For Me
							</button>
						</div>
					))}
				</div>
			</div>
		);
	};

	render() {
		const {
			job,
			gapData,
			isLoadingGaps,
		} = this.state;

		if (!job) {
			return null;
		}

		const allTags = [
			...(job.skills || []),
			...(job.tools || []),
			...(job.domains || []),
			...(job.softSkills || []),
			...(job.certificates || []),
		].slice(0, 12);

		return (
			<div className={styles.pageContainer}>
				<Sidebar />

				<div className={styles.glowTop} />
				<div className={styles.glowBottom} />

				<div className={styles.layoutShell}>
					<section className={styles.headerPanel}>
						<div className={styles.brandBadge}>HQVibe</div>
						<h1 className={styles.title}>{job.title || 'Role Details'}</h1>
						<p className={styles.subtitle}>{job.summary || 'No role summary available.'}</p>

						<div className={styles.metaGrid}>
							<div className={styles.metaCard}>
								<span className={styles.metaLabel}>Location</span>
								<strong className={styles.metaValue}>{this.formatLocation(job)}</strong>
							</div>

							<div className={styles.metaCard}>
								<span className={styles.metaLabel}>Employment</span>
								<strong className={styles.metaValue}>{job.employmentType || 'Not listed'}</strong>
							</div>

							<div className={styles.metaCard}>
								<span className={styles.metaLabel}>Workplace</span>
								<strong className={styles.metaValue}>{job.workPlaceType || 'Not listed'}</strong>
							</div>

							<div className={styles.metaCard}>
								<span className={styles.metaLabel}>Compensation</span>
								<strong className={styles.metaValue}>{this.formatCompensation(job)}</strong>
							</div>
						</div>
					</section>

					<section className={styles.mainCardPanel}>
						<div className={styles.buttonRow}>
							<button type='button' className={styles.secondaryButton} onClick={this.goBack}>Back To Jobs</button>
							{
								job.applyUrl
									? (
										<button
											type='button'
											className={styles.primaryButton}
											onClick={() => window.open(job.applyUrl, '_blank', 'noopener,noreferrer')}
										>
											Apply Now
										</button>
									)
									: null
							}
						</div>

						{
							allTags.length > 0 &&
							<div className={styles.tagWrap}>
								{allTags.map((tag, index) => (
									<span key={`job-tag-${tag}-${index}`} className={styles.tag}>{tag}</span>
								))}
							</div>
						}

						<div className={styles.gapsPanel}>
							<h3 className={styles.sectionTitle}>Readiness Gap Insights</h3>
							<p className={styles.sectionText}>
								These insights are available when the role is in your personalized recommendation pipeline.
							</p>

							{
								isLoadingGaps
									? <div className={styles.stateBox}>Loading readiness insights...</div>
									: null
							}

							{
								!isLoadingGaps && !gapData
									? <div className={styles.stateBox}>No recommendation gap data found for this role yet.</div>
									: null
							}

							{
								!isLoadingGaps && gapData &&
								<div className={styles.gapGrid}>
									{this.renderGapList('Skills', gapData.skillGaps)}
									{this.renderGapList('Tools', gapData.toolGaps)}
									{this.renderGapList('Domains', gapData.domainGaps)}
									{this.renderGapList('Soft Skills', gapData.softSkillGaps)}
									{this.renderGapList('Certificates', gapData.certificateGaps)}
								</div>
							}
						</div>
					</section>
				</div>
			</div>
		);
	}
}
